import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatPrice, getImageUrl } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { normalizeNumberInput, sanitizeLeadingZeroInput } from "@/lib/utils";
import { Search, ArrowLeft, Plus } from "lucide-react";

const DISCOUNT_ROLES = ["reseller", "agen", "distributor", "produsen"];
const ROLE_ALIASES = { mitra: "produsen" };

const canonicalDiscountRole = (role = "") => ROLE_ALIASES[String(role || "").trim().toLowerCase()] || String(role || "").trim().toLowerCase();

const normalizeMitraDiscountFields = (data = {}) => {
  if (!data || typeof data !== "object") return {};
  const normalized = { ...data };
  const mitraValue = normalized.discount_mitra_value;
  const mitraLegacyValue = normalized.discount_mitra;
  const produsenValue = normalized.discount_produsen_value;
  const produsenLegacyValue = normalized.discount_produsen;

  if ((mitraValue === undefined || mitraValue === null || mitraValue === "") && (mitraLegacyValue === undefined || mitraLegacyValue === null || mitraLegacyValue === "")) {
    const fallbackValue = produsenValue ?? produsenLegacyValue;
    if (fallbackValue !== undefined && fallbackValue !== null && fallbackValue !== "") {
      normalized.discount_mitra_value = fallbackValue;
      normalized.discount_mitra = fallbackValue;
    }
  }

  if (!normalized.discount_mitra_type) {
    normalized.discount_mitra_type = normalized.discount_produsen_type || "percent";
  }

  return normalized;
};

const resolveDiscountConfig = (source, role, isBrand = false) => {
  role = canonicalDiscountRole(role);
  if (!source || !DISCOUNT_ROLES.includes(role)) return { type: "percent", value: 0, is_set: false };

  const normalizedSource = role === "produsen" ? normalizeMitraDiscountFields(source) : source;
  let legacyField = `discount_${role}`;
  let fallbackField = null;
  if (isBrand && role === "agen") legacyField = "discount_agent";
  if (role === "produsen") {
    legacyField = "discount_mitra";
    fallbackField = "discount_produsen";
  }

  const typeField = `${legacyField}_type`;
  const valueField = `${legacyField}_value`;
  const fallbackTypeField = fallbackField ? `${fallbackField}_type` : null;
  const fallbackValueField = fallbackField ? `${fallbackField}_value` : null;

  let discountType = (normalizedSource[typeField] || (fallbackTypeField ? normalizedSource[fallbackTypeField] : null) || "percent").toString().trim().toLowerCase();
  if (!["percent", "fixed"].includes(discountType)) discountType = "percent";

  let rawValue = normalizedSource[valueField];
  if (rawValue === undefined || rawValue === null || rawValue === "") rawValue = normalizedSource[legacyField];
  if ((rawValue === undefined || rawValue === null || rawValue === "") && fallbackValueField) {
    rawValue = normalizedSource[fallbackValueField];
    discountType = ((fallbackTypeField ? normalizedSource[fallbackTypeField] : null) || "percent").toString().trim().toLowerCase();
  }

  const isSet = rawValue !== undefined && rawValue !== null && rawValue !== "";
  const discountValue = Math.max(0, Number(rawValue || 0));
  return { type: discountType, value: discountValue, is_set: isSet };
};

const resolveVariantDiscountConfig = (variant, role) => {
  role = canonicalDiscountRole(role);
  if (!variant || !DISCOUNT_ROLES.includes(role)) return { type: "percent", value: 0, is_set: false };
  const fieldRole = role === "produsen" ? "mitra" : role;
  const typeField = `discount_${fieldRole}_type`;
  const valueField = `discount_${fieldRole}_value`;
  const rawValue = variant[valueField];
  if (rawValue === undefined || rawValue === null || rawValue === "") return { type: "percent", value: 0, is_set: false };
  let discountType = (variant[typeField] || "percent").toString().trim().toLowerCase();
  if (!["percent", "fixed"].includes(discountType)) discountType = "percent";
  return { type: discountType, value: Math.max(0, Number(rawValue || 0)), is_set: true };
};

const computeRolePricing = (product, brand = null, role = "retail", basePriceOverride = null, sizePriceAdjustment = 0, variant = null) => {
  role = canonicalDiscountRole(role);
  const basePrice = Number(basePriceOverride ?? product?.price ?? 0) || 0;
  const sizeAdjustment = Math.max(0, Number(sizePriceAdjustment || 0));
  if (!DISCOUNT_ROLES.includes(role)) {
    return { base_price: basePrice, size_price_adjustment: sizeAdjustment, final_price: Math.max(0, Math.round(basePrice + sizeAdjustment)), savings: 0, discount_type: "percent", discount_value: 0, discount_source: "none" };
  }

  const variantDiscount = resolveVariantDiscountConfig(variant, role);
  const productDiscount = resolveDiscountConfig(product, role, false);
  const brandDiscount = resolveDiscountConfig(brand, role, true);
  const activeDiscount = variantDiscount.is_set ? variantDiscount : (productDiscount.is_set ? productDiscount : (brandDiscount.is_set ? brandDiscount : { type: "percent", value: 0 }));
  const discountSource = variantDiscount.is_set ? "variant" : (productDiscount.is_set ? "product" : (brandDiscount.is_set ? "brand" : "none"));
  const savings = activeDiscount.type === "fixed"
    ? Math.min(Math.round(basePrice), Math.max(0, Math.round(activeDiscount.value || 0)))
    : Math.min(Math.round(basePrice), Math.max(0, Math.round(basePrice * ((Number(activeDiscount.value || 0)) / 100))));

  return {
    base_price: basePrice,
    size_price_adjustment: sizeAdjustment,
    final_price: Math.max(0, Math.round(basePrice - savings + sizeAdjustment)),
    savings,
    discount_type: activeDiscount.type,
    discount_value: Number(activeDiscount.value || 0),
    discount_source: discountSource,
  };
};

export default function AdminCreateOrder() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    user_id: "",
    notes: "",
    payment_method: "bank_transfer",
    shipping_mode: "later",
    recipient_info: { name: "", phone: "", address: "" },
    additional_fee: 0,
    additional_discount: 0,
    payment_mode: "none", // none | dp | full
    initial_payment_nominal: 0,
    initial_payment_notes: "",
  });
  const [createUsers, setCreateUsers] = useState([]);
  const [createProducts, setCreateProducts] = useState([]);
  const [createBrands, setCreateBrands] = useState([]);
  const [createSelectedUser, setCreateSelectedUser] = useState(null);
  const [createSelectedProduct, setCreateSelectedProduct] = useState(null);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [createVariantRows, setCreateVariantRows] = useState([
    { id: 1, variant: { style: '', color: '', size: '' }, quantity: 1 }
  ]);

  useEffect(() => {
    // Fetch users and products in parallel
    const fetchData = async () => {
      try {
        const [usersRes, productsRes, brandsRes] = await Promise.all([
          API.get("/admin/users"),
          API.get("/products?limit=1000"),
          API.get("/brands"),
        ]);
        setCreateUsers(usersRes.data.users || []);
        setCreateProducts(productsRes.data.products || []);
        setCreateBrands(brandsRes.data.brands || []);
      } catch { 
        toast.error("Failed to load data"); 
      }
    };
    fetchData();
  }, []);

  const getUserLabel = (user) => {
    if (!user) return "";
    return `${user.name || "Tanpa Nama"} (${user.email || "-"}) — ${user.role || "retail"}`;
  };

  const handleUserSelect = (userId) => {
    const user = createUsers.find((u) => u.user_id === userId);
    setCreateForm((f) => ({ ...f, user_id: userId }));
    setCreateSelectedUser(user || null);
  };

  const orderableCreateUsers = createUsers.filter((user) => String(user.role || "").toLowerCase() !== "admin");

  const filteredCreateUsers = orderableCreateUsers.filter((user) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return [user.name, user.email, user.role, user.phone]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q));
  });

  const getDefaultVariantStyle = (product = createSelectedProduct) => {
    const variants = product?.variants || [];
    return variants.find((v) => v.style)?.style || "";
  };

  const handleAddVariantRow = () => {
    setCreateVariantRows(prev => [
      ...prev,
      { id: Date.now(), variant: { style: getDefaultVariantStyle(), color: '', size: '' }, quantity: 1 }
    ]);
  };

  const handleRemoveVariantRow = (id) => {
    setCreateVariantRows(prev => prev.filter(r => r.id !== id));
  };

  const handleVariantRowChange = (id, field, value) => {
    setCreateVariantRows(prev =>
      prev.map(r => {
        if (r.id !== id) return r;
        const baseStyle = r.variant.style || getDefaultVariantStyle();
        if (field === 'style') return { ...r, variant: { style: value || baseStyle, color: '', size: '' } };
        if (field === 'color') return { ...r, variant: { ...r.variant, style: baseStyle, color: value, size: '' } };
        if (field === 'size') return { ...r, variant: { ...r.variant, style: baseStyle, size: value } };
        if (field === 'quantity') return { ...r, quantity: Math.max(1, value) };
        return r;
      })
    );
  };

  const handleQtyChange = (id, delta) => {
    setCreateVariantRows(prev =>
      prev.map(r => r.id === id ? { ...r, quantity: Math.max(1, r.quantity + delta) } : r)
    );
  };

  const handleProductSelect = (productId) => {
    const p = createProducts.find(p => p.id === productId);
    const defaultStyle = getDefaultVariantStyle(p);
    setCreateSelectedProduct(p || null);
    setCreateVariantRows([{ id: 1, variant: { style: defaultStyle, color: '', size: '' }, quantity: 1 }]);
  };

  const getBrandForProduct = (product) => createBrands.find((brand) => brand.id === product?.brand_id) || null;

  const getMatchedVariant = (variant = {}) => {
    const variants = createSelectedProduct?.variants || [];
    const effectiveStyle = variant.style || getDefaultVariantStyle();
    return variants.find((v) =>
      (!effectiveStyle || v.style === effectiveStyle) &&
      (!variant.color || v.color === variant.color) &&
      (!variant.size || v.size === variant.size)
    );
  };

  const getProductPricing = (product) => computeRolePricing(product, getBrandForProduct(product), createSelectedUser?.role, product?.price);

  const getRowPricing = (row) => {
    const matchedVariant = getMatchedVariant(row?.variant || {});
    const variantBasePrice = matchedVariant?.price ?? createSelectedProduct?.price ?? 0;
    const sizeAdjustment = matchedVariant?.size_price_adjustment ?? row?.variant?.size_price_adjustment ?? 0;
    return computeRolePricing(createSelectedProduct, getBrandForProduct(createSelectedProduct), createSelectedUser?.role, variantBasePrice, sizeAdjustment, matchedVariant);
  };

  const getProductDisplayPrice = (product) => Number(getProductPricing(product).final_price || 0);

  const getRowUnitPrice = (row) => Number(getRowPricing(row).final_price || 0);

  const validRows = createVariantRows.filter(r => r.quantity > 0);
  const itemsProductSubtotal = validRows.reduce((sum, row) => {
    const pricing = getRowPricing(row);
    return sum + (Number(pricing.base_price || 0) * Number(row.quantity || 0));
  }, 0);
  const sizeAdjustmentTotal = validRows.reduce((sum, row) => {
    const pricing = getRowPricing(row);
    return sum + (Number(pricing.size_price_adjustment || 0) * Number(row.quantity || 0));
  }, 0);
  const itemsBaseSubtotal = itemsProductSubtotal + sizeAdjustmentTotal;
  const roleDiscountTotal = validRows.reduce((sum, row) => sum + (Number(getRowPricing(row).savings || 0) * Number(row.quantity || 0)), 0);
  const itemsSubtotal = validRows.reduce((sum, row) => sum + (getRowUnitPrice(row) * Number(row.quantity || 0)), 0);
  const additionalFee = Number(createForm.additional_fee) || 0;
  const additionalDiscount = Number(createForm.additional_discount) || 0;
  const orderGrandTotal = Math.max(0, itemsSubtotal + additionalFee - additionalDiscount);
  const effectiveInitialPayment = createForm.payment_mode === "full"
    ? orderGrandTotal
    : (Number(createForm.initial_payment_nominal) || 0);

  const handleCreateOrder = async () => {
    if (!createForm.user_id) { toast.error("Pilih pemesan terlebih dahulu"); return; }
    if (!createSelectedProduct) { toast.error("Pilih produk terlebih dahulu"); return; }
    if (validRows.length === 0) { toast.error("Tambahkan minimal 1 varian dengan qty > 0"); return; }
    setCreateLoading(true);
    try {
      const payload = {
        user_id: createForm.user_id,
        items: validRows.map(r => ({
          product_id: createSelectedProduct.id,
          variant: r.variant,
          quantity: r.quantity,
          item_notes: "",
        })),
        payment_method: createForm.payment_method,
        notes: createForm.notes,
        shipping_mode: createForm.shipping_mode,
        recipient_info: createForm.recipient_info,
        additional_fee: additionalFee,
        additional_discount: additionalDiscount,
        initial_payments: [],
      };
      if (createForm.payment_mode !== "none" && effectiveInitialPayment > 0) {
        payload.initial_payments.push({
          nominal: effectiveInitialPayment,
          payment_type: "transfer",
          notes: createForm.initial_payment_notes || "",
        });
      }
      await API.post("/admin/orders/manual", payload);
      toast.success("Order berhasil dibuat!");
      navigate("/admin/orders");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat order");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <AdminLayout title="Tambah Order Manual">
      <div className="max-w-3xl mx-auto pb-10">
        <button 
          onClick={() => navigate("/admin/orders")}
          className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar Order
        </button>

        <div className="bg-white border border-stone-100 rounded-lg p-6 shadow-sm space-y-8">
          
          {/* Section 1: Pemesan */}
          <div className="border-b border-stone-100 pb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-stone-700 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#8A9A86] text-white flex items-center justify-center text-xs">1</span> 
              Pemesan
            </h3>
            <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
              <PopoverTrigger asChild>
                <button className="w-full md:w-2/3 flex items-center gap-2 px-3 py-2.5 border border-stone-200 rounded-md hover:border-stone-400 transition-colors text-left bg-white">
                  <Search className="w-4 h-4 text-stone-400 shrink-0" />
                  <span className={`text-sm truncate ${createSelectedUser ? "text-stone-800" : "text-stone-400"}`}>
                    {createSelectedUser ? getUserLabel(createSelectedUser) : "Cari nama / email reseller..."}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] max-w-[calc(100vw-2rem)] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Ketik nama, email, role, atau no HP..."
                    value={userSearch}
                    onValueChange={setUserSearch}
                    autoFocus
                  />
                  <CommandList>
                    <CommandEmpty>User tidak ditemukan</CommandEmpty>
                    {filteredCreateUsers.map((user) => (
                      <CommandItem
                        key={user.user_id}
                        value={user.user_id}
                        onSelect={() => {
                          handleUserSelect(user.user_id);
                          setUserSearchOpen(false);
                          setUserSearch("");
                        }}
                        className="flex items-start gap-3 cursor-pointer"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-800 truncate">{user.name || "Tanpa Nama"}</p>
                          <p className="text-xs text-stone-500 truncate">{user.email || "-"}</p>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-stone-500 bg-stone-100 px-2 py-1 rounded-full shrink-0">
                          {user.role || "retail"}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {createSelectedUser && (
              <div className="mt-3 bg-stone-50 p-3 rounded-md border border-stone-100 w-full md:w-2/3">
                <p className="text-sm text-stone-600">
                  <span className="text-stone-400">Role:</span> <span className="font-semibold capitalize text-stone-800">{createSelectedUser.role}</span>
                  {createSelectedUser.role !== "retail" && <span className="ml-2 text-emerald-600 text-xs">✨ Harga Partner Aktif</span>}
                </p>
                <p className="text-sm text-stone-600 mt-1"><span className="text-stone-400">No HP:</span> {createSelectedUser.phone || "-"}</p>
              </div>
            )}
          </div>

          {/* Section 2: Items */}
          <div className="border-b border-stone-100 pb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-stone-700 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#8A9A86] text-white flex items-center justify-center text-xs">2</span> 
              Item Order
            </h3>

            {!createSelectedProduct ? (
              <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                <PopoverTrigger asChild>
                  <button className="w-full md:w-2/3 flex items-center gap-2 px-3 py-2.5 border border-stone-200 rounded-md hover:border-stone-400 transition-colors text-left bg-white">
                    <Search className="w-4 h-4 text-stone-400 shrink-0" />
                    <span className="text-sm text-stone-400">{productSearch || "Cari dan pilih produk..."}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Ketik nama produk..."
                      value={productSearch}
                      onValueChange={setProductSearch}
                      autoFocus
                    />
                    <CommandList>
                      <CommandEmpty>Produk tidak ditemukan</CommandEmpty>
                      {createProducts
                        .filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()))
                        .map(p => (
                          <CommandItem
                            key={p.id}
                            value={p.id}
                            onSelect={() => {
                              handleProductSelect(p.id);
                              setProductSearchOpen(false);
                              setProductSearch("");
                            }}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <span className="text-sm font-medium text-stone-800">{p.name}</span>
                            <span className="text-xs text-stone-400">({p.stock_type})</span>
                            <div className="ml-auto text-right">
                              {Number(getProductPricing(p).savings || 0) > 0 && (
                                <div className="text-[10px] text-stone-400 line-through">{formatPrice(getProductPricing(p).base_price)}</div>
                              )}
                              <div className="text-xs text-[#8A9A86] font-semibold">{formatPrice(getProductDisplayPrice(p))}</div>
                            </div>
                          </CommandItem>
                        ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="space-y-3 w-full">
                <div className="flex items-center gap-3 bg-[#8A9A86]/10 border border-[#8A9A86]/20 rounded-md px-4 py-3">
                  <div className="w-10 h-10 bg-white rounded border border-stone-200 overflow-hidden shrink-0">
                    {createSelectedProduct.images?.[0] ? (
                      <img src={getImageUrl(createSelectedProduct.images[0])} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300 text-xs">No Img</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-bold text-stone-800 block">{createSelectedProduct.name}</span>
                    <span className="text-xs text-stone-500">{createSelectedProduct.stock_type}</span>
                    {Number(getProductPricing(createSelectedProduct).savings || 0) > 0 && (
                      <span className="text-[11px] text-stone-400 line-through block mt-1">{formatPrice(getProductPricing(createSelectedProduct).base_price)}</span>
                    )}
                    <span className="text-xs font-semibold text-[#8A9A86] block mt-1">Harga role: {formatPrice(getProductDisplayPrice(createSelectedProduct))}</span>
                  </div>
                  <button
                    onClick={() => { setCreateSelectedProduct(null); setCreateVariantRows([{ id: 1, variant: { style: '', color: '', size: '' }, quantity: 1 }]); }}
                    className="text-xs text-rose-600 hover:text-rose-700 font-medium px-2 py-1 hover:bg-rose-50 rounded transition-colors"
                  >
                    Ganti Produk
                  </button>
                </div>

                {/* Variant rows */}
                <div className="space-y-2 mt-4">
                  {createVariantRows.map((row) => {
                    const variants = createSelectedProduct?.variants || [];
                    const styles = [...new Map(variants.filter(v => v.style).map(v => [v.style, v])).values()];
                    const effectiveStyle = row.variant.style || getDefaultVariantStyle();
                    const colorsForStyle = [...new Map(variants.filter(v => (!effectiveStyle || v.style === effectiveStyle) && v.color).map(v => [v.color, v])).values()];
                    const sizesForCombo = [...new Map(variants.filter(v => (!effectiveStyle || v.style === effectiveStyle) && (!row.variant.color || v.color === row.variant.color) && v.size).map(v => [v.size, v])).values()];
                    return (
                      <div key={row.id} className="flex flex-wrap gap-3 items-center bg-stone-50 border border-stone-200 rounded-md p-3">
                        {styles.length > 0 ? (
                          <Select value={effectiveStyle || styles[0]?.style || ""} onValueChange={(val) => handleVariantRowChange(row.id, "style", val)}>
                            <SelectTrigger className="h-9 text-sm w-40 bg-white"><SelectValue placeholder="Style" /></SelectTrigger>
                            <SelectContent>{styles.map(v => <SelectItem key={v.style} value={v.style}>{v.style}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : null}
                        {colorsForStyle.length > 0 ? (
                          <Select value={row.variant.color || "__all__"} onValueChange={(val) => handleVariantRowChange(row.id, "color", val === "__all__" ? "" : val)}>
                            <SelectTrigger className="h-9 text-sm w-40 bg-white"><SelectValue placeholder="Warna" /></SelectTrigger>
                            <SelectContent><SelectItem value="__all__">Semua Warna</SelectItem>{colorsForStyle.map(v => <SelectItem key={v.color} value={v.color}>{v.color}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : null}
                        {sizesForCombo.length > 0 ? (
                          <Select value={row.variant.size || "__all__"} onValueChange={(val) => handleVariantRowChange(row.id, "size", val === "__all__" ? "" : val)}>
                            <SelectTrigger className="h-9 text-sm w-32 bg-white"><SelectValue placeholder="Ukuran" /></SelectTrigger>
                            <SelectContent><SelectItem value="__all__">Semua Ukuran</SelectItem>{sizesForCombo.map(v => <SelectItem key={v.size} value={v.size}>{v.size}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : null}
                        
                        <div className="flex items-center gap-2 ml-auto">
                          {createSelectedProduct && (
                            <div className="mr-4 text-right">
                              {Number(getRowPricing(row).savings || 0) > 0 && (
                                <div className="text-[10px] text-stone-400 line-through">
                                  {formatPrice((Number(getRowPricing(row).base_price || 0) + Number(getRowPricing(row).size_price_adjustment || 0)) * row.quantity)}
                                </div>
                              )}
                              <div className="text-sm font-bold text-[#8A9A86]">
                                {formatPrice(getRowUnitPrice(row) * row.quantity)}
                              </div>
                              {Number(getRowPricing(row).size_price_adjustment || 0) > 0 && (
                                <div className="text-[10px] text-stone-500">Termasuk tambahan size +{formatPrice(getRowPricing(row).size_price_adjustment)}</div>
                              )}
                            </div>
                          )}
                          <div className="flex items-center bg-white border border-stone-200 rounded">
                            <button onClick={() => handleQtyChange(row.id, -1)} className="w-8 h-8 flex items-center justify-center text-stone-600 hover:bg-stone-100 text-base font-bold transition-colors">-</button>
                            <span className="w-10 text-center text-sm font-semibold border-x border-stone-200 h-8 flex items-center justify-center">{row.quantity}</span>
                            <button onClick={() => handleQtyChange(row.id, 1)} className="w-8 h-8 flex items-center justify-center text-stone-600 hover:bg-stone-100 text-base font-bold transition-colors">+</button>
                          </div>
                          {createVariantRows.length > 1 && (
                            <button onClick={() => handleRemoveVariantRow(row.id)} className="text-stone-400 hover:text-rose-500 text-sm p-2 transition-colors">✕</button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <button onClick={handleAddVariantRow} className="text-sm text-[#8A9A86] hover:text-[#7a8a76] font-semibold flex items-center gap-1.5 mt-2 transition-colors">
                    <Plus className="w-4 h-4" /> Tambah Varian Lainnya
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Biaya & Diskon */}
          <div className="border-b border-stone-100 pb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-stone-700 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#8A9A86] text-white flex items-center justify-center text-xs">3</span> 
              Biaya & Diskon Tambahan
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-stone-600 mb-1.5 block">Biaya Tambahan (Rp)</Label>
                <Input
                  type="number"
                  min={0}
                  value={createForm.additional_fee ?? ""}
                  onChange={(e) => setCreateForm(f => ({ ...f, additional_fee: sanitizeLeadingZeroInput(e.target.value) }))}
                  onBlur={(e) => setCreateForm(f => ({ ...f, additional_fee: normalizeNumberInput(e.target.value, 0) }))}
                  className="h-10 text-sm"
                  placeholder="Contoh: 15000"
                />
              </div>
              <div>
                <Label className="text-sm text-stone-600 mb-1.5 block">Diskon Tambahan (Rp)</Label>
                <Input
                  type="number"
                  min={0}
                  value={createForm.additional_discount ?? ""}
                  onChange={(e) => setCreateForm(f => ({ ...f, additional_discount: sanitizeLeadingZeroInput(e.target.value) }))}
                  onBlur={(e) => setCreateForm(f => ({ ...f, additional_discount: normalizeNumberInput(e.target.value, 0) }))}
                  className="h-10 text-sm"
                  placeholder="Contoh: 50000"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Perhitungan Total */}
          <div className="border-b border-stone-100 pb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-stone-700 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#8A9A86] text-white flex items-center justify-center text-xs">4</span>
              Perhitungan Harga Total
            </h3>

            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="text-stone-500">Subtotal Harga Produk</span>
                <span className="font-semibold text-stone-800">{formatPrice(itemsProductSubtotal)}</span>
              </div>
              {sizeAdjustmentTotal > 0 && (
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-stone-500">Tambahan Harga Size/Varian</span>
                  <span className="font-semibold text-amber-700">+ {formatPrice(sizeAdjustmentTotal)}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="text-stone-500">Subtotal Normal</span>
                <span className={`font-semibold ${roleDiscountTotal > 0 ? "text-stone-400 line-through" : "text-stone-800"}`}>{formatPrice(itemsBaseSubtotal)}</span>
              </div>
              {roleDiscountTotal > 0 && (
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-stone-500">Diskon Role</span>
                  <span className="font-semibold text-emerald-600">- {formatPrice(roleDiscountTotal)}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="text-stone-500">Subtotal Setelah Diskon</span>
                <span className="font-semibold text-stone-800">{formatPrice(itemsSubtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="text-stone-500">Biaya Tambahan</span>
                <span className="font-semibold text-stone-800">{formatPrice(additionalFee)}</span>
              </div>
              <div className="flex items-center justify-between gap-4 text-sm">
                <span className="text-stone-500">Diskon Tambahan</span>
                <span className="font-semibold text-rose-600">- {formatPrice(additionalDiscount)}</span>
              </div>
              <div className="pt-3 border-t border-stone-200 flex items-center justify-between gap-4">
                <span className="text-sm font-bold uppercase tracking-wider text-stone-700">Grand Total</span>
                <span className="text-lg font-bold text-[#8A9A86]">{formatPrice(orderGrandTotal)}</span>
              </div>
            </div>
          </div>

          {/* Section 5: Pembayaran & Catatan */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-stone-700 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#8A9A86] text-white flex items-center justify-center text-xs">5</span> 
              Pembayaran Awal & Catatan
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              {["none", "dp", "full"].map(mode => (
                <button
                  key={mode}
                  onClick={() => setCreateForm(f => ({ ...f, payment_mode: mode, initial_payment_nominal: mode === "none" ? 0 : mode === "full" ? 0 : f.initial_payment_nominal }))}
                  className={`py-2.5 text-sm font-bold rounded-md border-2 transition-colors ${createForm.payment_mode === mode ? "bg-[#8A9A86]/10 text-[#8A9A86] border-[#8A9A86]" : "bg-white text-stone-500 border-stone-200 hover:border-stone-300"}`}
                >
                  {mode === "none" ? "Belum Bayar" : mode === "dp" ? "Bayar Sebagian (DP)" : "Sudah Lunas"}
                </button>
              ))}
            </div>

            {createForm.payment_mode !== "none" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 p-4 bg-stone-50 rounded-lg border border-stone-100">
                <div>
                  <Label className="text-sm text-stone-600 mb-1.5 block">
                    Nominal Pembayaran {createForm.payment_mode === "full" ? "(Otomatis Lunas)" : "(Rp)"}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={createForm.payment_mode === "full" ? effectiveInitialPayment : (createForm.initial_payment_nominal ?? "")}
                    onChange={(e) => setCreateForm(f => ({ ...f, initial_payment_nominal: sanitizeLeadingZeroInput(e.target.value) }))}
                    onBlur={(e) => setCreateForm(f => ({ ...f, initial_payment_nominal: normalizeNumberInput(e.target.value, 0) }))}
                    className="h-10 text-sm bg-white"
                    placeholder={createForm.payment_mode === "full" ? "Total Harga" : "Masukkan nominal DP..."}
                    disabled={createForm.payment_mode === "full"}
                  />
                </div>
                <div>
                  <Label className="text-sm text-stone-600 mb-1.5 block">Metode / Catatan Pembayaran</Label>
                  <Input
                    value={createForm.initial_payment_notes || ""}
                    onChange={(e) => setCreateForm(f => ({ ...f, initial_payment_notes: e.target.value }))}
                    className="h-10 text-sm bg-white"
                    placeholder="Contoh: Transfer BCA an. Budi"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm text-stone-600 mb-1.5 block">Catatan Tambahan Order</Label>
              <Input
                value={createForm.notes || ""}
                onChange={(e) => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                className="h-10 text-sm"
                placeholder="Catatan opsional (misal: bungkus kado, dropship, dll)..."
              />
            </div>
          </div>

          <div className="pt-6 mt-4 border-t border-stone-100 flex gap-3">
            <button
              onClick={() => navigate("/admin/orders")}
              className="flex-1 py-3 text-sm font-bold rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors"
              disabled={createLoading}
            >
              Batal
            </button>
            <button
              onClick={handleCreateOrder}
              disabled={createLoading || !createForm.user_id || !createSelectedProduct}
              className="flex-[2] py-3 text-sm font-bold rounded-md bg-[#8A9A86] text-white hover:bg-[#7a8a76] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createLoading ? "Menyimpan Order..." : "Simpan & Buat Order"}
            </button>
          </div>

        </div>
      </div>
    </AdminLayout>
  );
}
